import { Section, Table, WalletTokenListItem } from '@/components';
import {
  TableCellProps,
  TableRowProps,
} from '@/components/molecules/Table/interfaces/Table';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import {
  formatNumberToAmountInUsdString,
  formatNumberToAPYString,
} from '@/utils/formatUtils';
import { REWARDS } from '@/utils/strings';
import { FC, useCallback, useMemo, useState } from 'react';
interface Props {
  noOfItemsToShow: number;
  showShortList?: boolean;
  showSection?: boolean;
  showColumnHeaders?: boolean;
}
const PortfolioTokensAndUnderlyingYieldBearingAssetsListSection: FC<Props> = ({
  noOfItemsToShow,
  showShortList = false,
  showSection = true,
  showColumnHeaders = true,
}) => {
  const {
    tokensAndUnderlyingYieldBearingAssets: combinedItems,
    getDisplayedItems,
  } = useWalletsContext();
  const [sortColumn, setSortColumn] = useState<number>(1);
  const [isAscending, setIsAscending] = useState<boolean>(false);
  const handleSort = useCallback(
    (columnIndex: number) => {
      setIsAscending((prevAscending) =>
        sortColumn === columnIndex ? !prevAscending : true,
      );
      setSortColumn(columnIndex);
    },
    [sortColumn],
  );
  const sortItems = useCallback(
    (items: any[], columnIndex: number, ascending: boolean) => {
      return [...items].sort((a, b) => {
        const valueA = getSortValue(a, columnIndex);
        const valueB = getSortValue(b, columnIndex);
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return ascending
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        }
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return ascending ? valueA - valueB : valueB - valueA;
        }
        return 0;
      });
    },
    [],
  );
  const getSortValue = (item: any, columnIndex: number) => {
    switch (columnIndex) {
      case 0:
        return item.symbol || '';
      case 1:
        return item.balanceInUsd || 0;
      case 2:
        return item.rewardsInUsd || 0;
      case 3:
        return item.shareOfPortfolio || 0;
      case 4:
        return item.priceInUsd || 0;
      default:
        return '';
    }
  };
  const generateRows = useCallback(
    (items: any[], isShortList: boolean): TableRowProps[] =>
      items.map((item) => {
        const balance = item.balance ? item.balance.toFixed(2) : '0.00';
        const rewards = item.rewards ? item.rewards.toFixed(2) : '0.00';
        let balanceSubText = '';
        if (item.underlyingAssets && item.underlyingAssets.length > 0) {
          balanceSubText = item.underlyingAssets
            .map((asset: any) => {
              const assetBalance =
                typeof asset.balance === 'number'
                  ? asset.balance
                  : parseFloat(asset.balance || '0');
              return `${assetBalance.toFixed(2)} ${
                asset.symbol
              } (${formatNumberToAmountInUsdString(asset.balanceInUsd ?? 0)})`;
            })
            .join('\n');
        } else {
          balanceSubText = `${balance} ${item.symbol}`;
        }
        const cells: TableCellProps[] = [
          {
            mainText: (
              <WalletTokenListItem
                token={item}
                mainText={item.name}
                showNetwork
                showNetworkPrefix
                showSubNetworkNextToMainNetwork
                showTicker={false}
                showWalletAddress={false}
                showWalletAddressCopyButton={false}
                showBalance={false}
                leftColor={item.color}
                chipText={
                  item.apy !== undefined
                    ? formatNumberToAPYString(item.apy)
                    : undefined
                }
              />
            ),
            subText: '',
            align: 'left',
          },
          {
            mainText: formatNumberToAmountInUsdString(item.balanceInUsd ?? 0),
            subText: balanceSubText,
            align: isShortList ? 'right' : 'left',
          },
        ];
        if (!isShortList) {
          let rewardsSubText = '';
          if (item.isUnderlyingYieldBearingAsset) {
            if (item.underlyingAssets && item.underlyingAssets.length > 0) {
              rewardsSubText = item.underlyingAssets
                .map((asset: any) => {
                  const assetRewards =
                    typeof asset.rewards === 'number'
                      ? asset.rewards
                      : parseFloat(asset.rewards || '0');
                  return `${assetRewards.toFixed(2)} ${
                    asset.symbol
                  } (${formatNumberToAmountInUsdString(
                    asset.rewardsInUsd ?? 0,
                  )})`;
                })
                .join('\n');
            } else {
              rewardsSubText = `${rewards} ${item.symbol}`;
            }
          }
          cells.push({
            mainText: item.isUnderlyingYieldBearingAsset
              ? formatNumberToAmountInUsdString(item.rewardsInUsd ?? 0)
              : '-',
            subText: rewardsSubText,
            align: 'left',
          });
        }
        if (!isShortList) {
          cells.push(
            {
              mainText: `${((item.shareOfPortfolio ?? 0) * 100).toFixed(2)}%`,
              subText: '',
              align: 'left',
            },
            {
              mainText: formatNumberToAmountInUsdString(item.priceInUsd ?? 0),
              subText: '',
              align: 'left',
            },
          );
        }
        return { cells };
      }),
    [],
  );
  const displayedItems = useMemo(() => {
    const items = showShortList
      ? getDisplayedItems(noOfItemsToShow)
      : combinedItems;
    return sortItems(items, sortColumn, isAscending);
  }, [
    combinedItems,
    getDisplayedItems,
    isAscending,
    noOfItemsToShow,
    showShortList,
    sortColumn,
    sortItems,
  ]);
  const columnHeaders = showShortList
    ? ['Asset', { value: 'Balance', align: 'right' as 'right' }]
    : ['Asset', 'Balance', REWARDS.LABEL, 'Allocation', 'Price'];
  const columnWidths = showShortList ? [65, 35] : [35, 20, 20, 15, 10];
  const sortable = columnHeaders.map(() => true);
  const table = (
    <Table
      columnHeaders={columnHeaders}
      columnWidths={columnWidths}
      rows={generateRows(displayedItems, showShortList)}
      sortable={sortable}
      onSort={handleSort}
      sortedColumn={sortColumn}
      isAscending={isAscending}
      showColumnHeaders={showColumnHeaders}
    />
  );
  if (showSection) {
    return <Section headerTitle="Assets" body={table} />;
  }
  return table;
};
export default PortfolioTokensAndUnderlyingYieldBearingAssetsListSection;
